import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LinkDisabledDialogBoxComponent } from '../link-disabled-dialog-box/link-disabled-dialog-box.component';
import { PublishWithLinkDialogBoxComponent } from '../publish-with-link-dialog-box/publish-with-link-dialog-box.component';
import { GeneratAllQuizLink } from '@edtech-ui/core/models/admin-quiz-enhancement/get-all-generate-links';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AdminGenerateLinkStatus } from '@edtech-ui/constants/admin-link-status.enum';
import { DateService } from '@edtech-ui/core/services/dateService/date.service';
import { LinkEnabledDialogBoxComponent } from '../link-enabled-dialog-box/link-enabled-dialog-box.component';
import { InviteCandidateDialogBoxComponent } from '../invite-candidate-dialog-box/invite-candidate-dialog-box.component';
import { ExtendLinkDialogBoxComponent } from '../extend-link-dialog-box/extend-link-dialog-box.component';

@Component({
	selector: 'app-generate-link-detail-page',
	standalone: true,
	imports: [RouterModule, CommonModule, MatProgressSpinnerModule, InviteCandidateDialogBoxComponent, ExtendLinkDialogBoxComponent],
	templateUrl: './generate-link-detail-page.component.html',
	styleUrl: './generate-link-detail-page.component.scss'
})
export class GenerateLinkDetailPageComponent implements OnInit, OnDestroy {

	generateLinks: GeneratAllQuizLink[] = [];
	subscription = new Subscription();
	quizId!: string;
	isLoading = false;
	AdminGenerateLinkStatus = AdminGenerateLinkStatus;
	activeTab: AdminGenerateLinkStatus = AdminGenerateLinkStatus.ALL;
	page = 1;
	limit = 10;
	totalRecords = 0;
	totalPages = 0;
	visiblePages: number[] = [];
	dateTimeFormat = DateService.APP_DATE_TIME_BOTH;



	constructor(
		private toastr: ToastrService,
		private dialog: MatDialog,
		private quizService: QuizManagementService,
		private route: ActivatedRoute,
		private router: Router
	) { }


	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			const id = params.get('quizId');

			if (!id) return;
			this.quizId = id;
			this.getAllGenerativeLinks();
		});
	}



	copyLink(url: string) {
		navigator.clipboard.writeText(url).then(() => {
			this.toastr.success('URL Copied successfully');
		});
	}


	openDisableDialogBox(linkId: string) {
		const dialogRef = this.dialog.open(LinkDisabledDialogBoxComponent);

		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (result) {
				this.disabledGenerateLink(linkId)
			}
		})
	}


	disabledGenerateLink(linkId: string) {
		this.subscription.add(
			this.quizService.disableGenerateQuizLink(linkId).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Message);
						this.getAllGenerativeLinks();
					} else {
						this.toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this.toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		);
	}



	openEnableDialogBox(linkId: string) {
		const dialogRef = this.dialog.open(LinkEnabledDialogBoxComponent);
		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (result) {
				this.enableGenerateLink(linkId)
			}
		})
	}


	enableGenerateLink(linkId: string) {
		this.subscription.add(
			this.quizService.enableGenerateQuizLink(linkId).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Message);
						this.getAllGenerativeLinks();
					} else {
						this.toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this.toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		);
	}


	openGenerateLinkBox() {
		const dialogRef = this.dialog.open(PublishWithLinkDialogBoxComponent, {
			data: {
				quizId: this.quizId,
				fromParent: true
			}
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.getAllGenerativeLinks();
			}
		})
	}


	openExtendDialog(link: GeneratAllQuizLink) {
		const dialogRef = this.dialog.open(ExtendLinkDialogBoxComponent, {
			data: {
				linkId: link.link_id,
				linkName: link.link_name,
				currentExpiry: link.end_date
			},
			panelClass: 'custom-modalbox'
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.getAllGenerativeLinks();
			}
		});
	}

	openInviteDialogBox(link: GeneratAllQuizLink) {
		const dialogRef = this.dialog.open(InviteCandidateDialogBoxComponent, {
			data: {
				linkId: link.link_id,
				existingEmails: link.invited_emails
			}
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.getAllGenerativeLinks();
			}
		})
	}



	getAllGenerativeLinks() {
		if (!this.quizId) return;
		this.isLoading = true;
		this.subscription.add(
			this.quizService.getAllGenerateLinks(this.quizId, this.page, this.limit).pipe(
				tap(response => {
					if (response.Success) {
						this.generateLinks = response.Data.data;
						this.page = response.Data.pagination.page;
						this.limit = response.Data.pagination.limit;
						this.totalRecords = response.Data.pagination.totalLinks;
						this.totalPages = response.Data.pagination.totalPages;
						this.setupPagination();
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		);
	}


	setupPagination() {
		const pagesToShow = 5;

		let start = Math.max(1, this.page - 2);
		let end = Math.min(this.totalPages, start + pagesToShow - 1);
		if (end - start < pagesToShow - 1) {
			start = Math.max(1, end - pagesToShow + 1);
		}
		this.visiblePages = [];
		for (let i = start; i <= end; i++) {
			this.visiblePages.push(i);
		}
	}


	changePage(page: number) {
		if (page < 1 || page > this.totalPages) return;
		this.page = page;
		this.getAllGenerativeLinks();
	}


	get filteredLinks(): GeneratAllQuizLink[] {
		const now = new Date();

		return this.generateLinks.filter(link => {
			const start = new Date(link.start_date);
			const end = new Date(link.end_date);
			const status = link.status?.toLowerCase();

			if (this.activeTab === AdminGenerateLinkStatus.ALL) return true;

			if (this.activeTab === AdminGenerateLinkStatus.SCHEDULED) {
				return start > now;
			}

			if (this.activeTab === AdminGenerateLinkStatus.EXPIRED) {
				return status === AdminGenerateLinkStatus.EXPIRED;
			}

			if (this.activeTab === AdminGenerateLinkStatus.ACTIVE) {
				return status === AdminGenerateLinkStatus.ACTIVE;
			}

			if (this.activeTab === AdminGenerateLinkStatus.INACTIVE) {
				return status === AdminGenerateLinkStatus.INACTIVE;
			}
			return true;
		});
	}


	getCount(tab: AdminGenerateLinkStatus): number {
		const now = new Date();
		return this.generateLinks.filter(link => {
			const start = new Date(link.start_date);
			const status = link.status?.toLowerCase();

			if (tab === AdminGenerateLinkStatus.ALL) return true;

			if (tab === AdminGenerateLinkStatus.SCHEDULED) {
				return start > now;
			}

			if (tab === AdminGenerateLinkStatus.EXPIRED) {
				return status === AdminGenerateLinkStatus.EXPIRED;
			}

			if (tab === AdminGenerateLinkStatus.ACTIVE) {
				return status === AdminGenerateLinkStatus.ACTIVE;
			}

			if (tab === AdminGenerateLinkStatus.INACTIVE) {
				return status === AdminGenerateLinkStatus.INACTIVE;
			}
			return false;
		}).length;
	}


	get emptyMessage(): string {
		switch (this.activeTab) {
			case AdminGenerateLinkStatus.ACTIVE:
				return 'No active links available';
			case AdminGenerateLinkStatus.EXPIRED:
				return 'No expired links here';
			case AdminGenerateLinkStatus.SCHEDULED:
				return 'No scheduled links available';
			case AdminGenerateLinkStatus.INACTIVE:
				return 'No disabled links found';
			default:
				return 'No links here';
		}
	}

	goToAssessmentDetailPage(linkId: string) {
		this.router.navigate(['admin-student-platform/assesment-attempt-detail-page', linkId])
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	goBack() {
		const roleId = localStorage.getItem('role_id');
		const jobId = localStorage.getItem('job_id');
		if ((roleId === '8' || roleId === '14' || roleId === 'COMPANYADMIN') && jobId) {
			this.router.navigate(['/recruiter-dashboard/jobs', jobId], { queryParams: { subTab: 'assessment' } });
		} else {
			this.router.navigate(['/admin-student-platform/admin-quiz']);
		}
	}
}